import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { Context } from '../index';
import OilFillingService from '../services/OilFillingService';
import CorrectionModal from '../components/CorrectionModal';
import { printPdfSilent, downloadPdf } from '../utils/print-pdf';

/**
 * OilFillingById - рабочий экран разливщика масел.
 * Отображает граммовые позиции заказа, кнопку печати наряда и завершения.
 */
export default function OilFillingById() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { store } = useContext(Context);

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [printed, setPrinted] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [correctionOpen, setCorrectionOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setNotFound(false);
        OilFillingService.getById(id)
            .then(data => setOrder(data))
            .catch(err => {
                if (err?.response?.status === 404) setNotFound(true);
                else if (err?.response?.status === 409) {
                    // Нет граммовых позиций - странный случай, тоже покажем notFound
                    setNotFound(true);
                } else {
                    setError(err?.response?.data?.error || err?.message || 'Ошибка загрузки');
                }
            })
            .finally(() => setLoading(false));
    }, [id]);

    /** Печать наряда розлива: silent print -> fallback download. */
    async function handlePrint() {
        setPrinting(true);
        try {
            const { pdfBase64 } = await OilFillingService.print(id);
            try {
                await printPdfSilent(pdfBase64);
            } catch (printErr) {
                // Silent print не сработал - fallback на скачивание
                try { downloadPdf(pdfBase64, `order-${order.name}.pdf`); } catch (_) {}
            }
            setPrinted(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404 || status === 422) {
                alert('Заказ изменён или нет граммовых позиций. Вернитесь к списку.');
            } else {
                alert('Ошибка печати: ' + (err?.response?.data?.error || err?.message));
            }
        } finally {
            setPrinting(false);
        }
    }

    /** Завершить розлив: перевод заказа в «Ожидает сборки». */
    async function handleComplete() {
        if (!printed || completing) return;
        setCompleting(true);
        try {
            const res = await OilFillingService.complete(id);
            if (res?.alreadyCompleted) {
                // Дубль в TTL окне - молча идём дальше без alert
            }
            navigate('/oil-filling', { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                alert('Заказ уже не в работе. Кто-то перевёл его вручную.');
                navigate('/oil-filling', { replace: true });
            } else {
                alert('Не удалось завершить: ' + (err?.response?.data?.error || err?.message));
            }
        } finally {
            setCompleting(false);
        }
    }

    /** Выйти из аккаунта с подтверждением. */
    async function handleLogout() {
        if (!window.confirm('Выйти?')) return;
        try { await store.logout(); } catch (_) {}
        navigate('/login', { replace: true });
    }

    /** Отправить заказ на корректировку (throw прерывает закрытие модалки). */
    async function handleSubmitCorrection(reason) {
        await OilFillingService.correction(id, reason);
        setCorrectionOpen(false);
        navigate('/oil-filling', { replace: true });
    }

    if (notFound) {
        return (
            <Container maxWidth="sm">
                <Box py={8} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h5" align="center">
                        Заказ был изменён или забран другим сотрудником
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/oil-filling', { replace: true })}
                    >
                        К следующему
                    </Button>
                </Box>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box py={8} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress />
                    <Typography>Загрузка заказа...</Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm">
                <Box py={8} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
                    <Button variant="contained" color="primary" onClick={() => navigate('/oil-filling')}>
                        Назад
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!order) {
        return (
            <Container maxWidth="sm">
                <Box py={8} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h6" align="center">Заказ не найден</Typography>
                    <Button variant="contained" color="primary" onClick={() => navigate('/oil-filling')}>
                        Назад
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Box py={3}>
                <Box mb={3}>
                    <Typography variant="h4" gutterBottom>Заказ №{order.name}</Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Позиций: {order.positionsCount}
                    </Typography>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        Комментарий: {order.description || '—'}
                    </Typography>
                    {order.extraComment && (
                        <Typography variant="body2">
                            Комментарий к заказу: {order.extraComment}
                        </Typography>
                    )}
                </Box>

                <Box mb={3} display="flex" gap={2} justifyContent="space-between" flexWrap="wrap">
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setCorrectionOpen(true)}
                            disabled={completing || printing}
                        >
                            Корректировки
                        </Button>
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePrint}
                            disabled={printing || completing}
                        >
                            {printing ? 'Печать...' : (printed ? 'Перепечатать' : 'Распечатать')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleComplete}
                            disabled={!printed || completing}
                        >
                            {completing ? 'Сохраняем...' : 'Заказ готов'}
                        </Button>
                    </Box>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Артикул</TableCell>
                                <TableCell>Код</TableCell>
                                <TableCell>Наименование</TableCell>
                                <TableCell align="right">Количество</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.positions.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell>{p.article || '—'}</TableCell>
                                    <TableCell>{p.code || '—'}</TableCell>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell align="right">{p.quantity} {p.uom}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleLogout}
                    >
                        Выйти
                    </Button>
                </Box>
            </Box>

            <CorrectionModal
                open={correctionOpen}
                onClose={() => setCorrectionOpen(false)}
                orderName={order.name}
                onSubmit={handleSubmitCorrection}
            />
        </Container>
    );
}
